import type {
  IconButtonProps,
  IntrinsicSystemProps,
  MotionPropsFor,
} from '@hover/blueprint';
import {
  forwardRef,
  motion,
  system,
  transition,
  useBoolean,
  useEventListener,
  useTheme,
} from '@hover/blueprint';
import { iconArrowLeft, iconArrowRight } from '@hover/icons';
import { AnimatePresence } from 'framer-motion';
import React, { useCallback, useMemo } from 'react';

import { IconButton } from 'src/shared/components';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';
import { useIdleTimeout } from 'src/shared/hooks/useIdleTimeout';

import { isGeneratingImage, withHover } from '../../helpers';
import { useHomeownerAppContext } from '../../hooks';
import { isGenerated } from '../../types';

import { useFullscreenImageContents } from './context';
import { layoutBreakpoint, useFullscreenDepth } from './helpers';

const MotionIconButton = motion(IconButton);

interface CarouselArrowProps extends MotionPropsFor<IconButtonProps> {}

const CarouselArrow = forwardRef<CarouselArrowProps, 'button'>(
  ({ sx, ...props }, ref) => (
    <MotionIconButton
      colorScheme="light"
      iconSize="medium"
      size={{ base: 'medium', tablet: 'large' }}
      sx={{
        ...sx,
        background: {
          [layoutBreakpoint]: 'unset',
        },
        svg: {
          opacity: props.isDisabled ? 0 : 1,
          ...transition.easing250('opacity'),
        },
      }}
      variant={{ base: 'primary', [layoutBreakpoint]: 'ghost' }}
      zIndex={useFullscreenDepth()}
      {...props}
      ref={ref}
    />
  ),
);

const getAnimationProps = (direction: 'left' | 'right') =>
  ({
    animate: { x: 0, opacity: 1 },
    exit: { x: direction === 'left' ? '-100%' : '100%', opacity: 0 },
    initial: { x: direction === 'left' ? '-100%' : '100%', opacity: 0 },
  } satisfies Partial<CarouselArrowProps>);

interface CarouselControlsProps
  extends Pick<
    CarouselArrowProps,
    'top' | 'left' | 'right' | 'bottom' | 'gridRow' | 'gridColumn'
  > {
  idleTimeout?: number;
  /**
   * Whether to run presence animations on intial mount and render. This is
   * passed directly to `AnimatePresence`.
   */
  initial: boolean;
  isVisible?: boolean;
  onNextMouseEnter?: () => void;
  onPreviousMouseEnter?: () => void;
  onNextMouseLeave?: () => void;
  onPreviousMouseLeave?: () => void;
}

const CarouselControls = ({
  idleTimeout = 1.5,
  initial,
  isVisible = true,
  onNextMouseEnter,
  onNextMouseLeave,
  onPreviousMouseEnter,
  onPreviousMouseLeave,
  top,
  left,
  right,
  bottom,
  gridRow,
  gridColumn,
}: CarouselControlsProps) => {
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const { currentMenu } = useHomeownerAppContext();
  const { image, nextImage, previousImage, next, previous } =
    useFullscreenImageContents();

  const previousProps = useMemo(() => getAnimationProps('left'), []);
  const nextProps = useMemo(() => getAnimationProps('right'), []);

  const [isHovered, { on: onEnter, off: onLeave }] = useBoolean();
  const trackEnter = useCallback(
    (handler?: () => void) => () => {
      onEnter();
      handler?.();
    },
    [onEnter],
  );
  const trackLeave = useCallback(
    (handler?: () => void) => () => {
      onLeave();
      handler?.();
    },
    [onLeave],
  );

  const isIdle = useIdleTimeout({ duration: idleTimeout, pause: isHovered });

  const containerProps = {
    display: 'none',
    top,
    bottom,
    position: 'absolute',
    sx: withHover({ display: 'block' }),
    gridRow,
    gridColumn,
  } satisfies IntrinsicSystemProps<'div'>;

  const showControls = isVisible && (!isIdle || isHovered);

  useEventListener('keydown', event => {
    if (currentMenu) return;

    switch (event.key) {
      case 'ArrowLeft':
        previous();
        break;
      case 'ArrowRight':
        next();
        break;
      default:
    }
  });

  const { space } = useTheme();

  // In the desktop layout we nudge the icon button so that the icon (and not
  // the edge of the button) aligns with the edge of the container and therefore
  // other layout elements
  const nudge = space[400];

  const generatedImage = image.aiGeneratedImage;

  return (
    <>
      <system.div
        key="previous"
        transform={{
          base: 'translateY(-50%)',
          [layoutBreakpoint]: `translateY(-50%) translateX(-${nudge})`,
        }}
        {...containerProps}
        left={left}
      >
        <AnimatePresence initial={initial}>
          {showControls && (
            <CarouselArrow
              icon={iconArrowLeft}
              isDisabled={!previousImage}
              key="previous-arrow"
              label={t('fullscreenImage.previous')}
              onClick={previous}
              onMouseEnter={trackEnter(onPreviousMouseEnter)}
              onMouseLeave={trackLeave(onPreviousMouseLeave)}
              {...previousProps}
            />
          )}
        </AnimatePresence>
      </system.div>
      <system.div
        key="next"
        transform={{
          base: 'translateY(-50%)',
          [layoutBreakpoint]: `translateY(-50%) translateX(${nudge})`,
        }}
        {...containerProps}
        right={right}
      >
        <AnimatePresence initial={initial}>
          {showControls && (
            <CarouselArrow
              icon={iconArrowRight}
              isDisabled={
                !nextImage ||
                (isGenerated(generatedImage) &&
                  isGeneratingImage(generatedImage))
              }
              key="next-arrow"
              label={t('fullscreenImage.next')}
              onClick={next}
              onMouseEnter={trackEnter(onNextMouseEnter)}
              onMouseLeave={trackLeave(onNextMouseLeave)}
              {...nextProps}
            />
          )}
        </AnimatePresence>
      </system.div>
    </>
  );
};

export { CarouselControls };
